import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import { preloadImage } from "@/lib/imageReady";
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Copy, ImagePlus, Loader2, ShieldCheck, Store, X } from "lucide-react";

const WaMark = ({ size = 16, muted = false }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    className={muted ? "text-neutral-300" : undefined}
    aria-hidden="true"
  >
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.95 1.16-.17.2-.35.22-.65.07-.3-.15-1.13-.42-2.15-1.33-.79-.71-1.33-1.58-1.48-1.88-.15-.3-.02-.46.13-.61.15-.15.35-.42.52-.62.13-.15.2-.27.3-.47.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.46 0 1.46 1.06 2.86 1.21 3.06.15.2 2.1 3.2 5.08 4.37 2.98 1.16 3.32.98 3.92.93.6-.05 1.94-.79 2.21-1.55.27-.77.27-1.43.2-1.56-.08-.13-.28-.2-.58-.35zM12.04 21.5h-.01a9.42 9.42 0 01-4.8-1.31l-.34-.2-3.57.93.96-3.48-.23-.36a9.4 9.4 0 01-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.99 6.67 2.77a9.37 9.37 0 012.76 6.68c0 5.2-4.24 9.43-9.45 9.43zM20.5 3.49A11.32 11.32 0 0012.04 0C5.79 0 .71 5.08.71 11.33c0 2 .52 3.95 1.52 5.67L0 24l7.17-1.88a11.3 11.3 0 004.86 1.11h.01c6.25 0 11.33-5.08 11.33-11.33 0-3.03-1.18-5.87-3.32-8.01z" />
  </svg>
);

/** Icons cropped from the supplied reference design — no substitutes. */
const APPS = [
  { id: "gpay", name: "Google Pay", src: "/upi/gpay.png", scheme: (q) => `tez://upi/pay?${q}` },
  { id: "phonepe", name: "PhonePe", src: "/upi/phonepe.png", scheme: (q) => `phonepe://pay?${q}` },
  { id: "paytm", name: "Paytm", src: "/upi/paytm.png", scheme: (q) => `paytmmp://pay?${q}` },
  { id: "bhim", name: "BHIM UPI", src: "/upi/bhim.png", scheme: (q) => `upi://pay?${q}` },
  { id: "amazonpay", name: "Amazon Pay", src: "/upi/amazonpay.png", scheme: (q) => `upi://pay?${q}` },
  { id: "more", name: "More UPI Apps", src: "/upi/more.png", scheme: (q) => `upi://pay?${q}` },
];

// The app icons and wordmark are warmed as soon as this module loads, so the
// checkout sheet opens with every logo already drawn instead of popping them
// in one by one.
[...APPS.map((a) => a.src), "/upi/udyog-word.png"].forEach(preloadImage);

// Whole rupees, always. Fees are flat now — a trailing ".00" only makes a
// student pause and re-read the figure.
const money = (n) => String(Math.round(Number(n) * 100) / 100).replace(/\.00$/, "");

function Shell({ children, wide, inMeeting, onBack }) {
  // Rendered at the document root so the meeting's layout can never clip it,
  // and never a browser popup — Chrome has nothing to block. The class stays
  // mounted and connected underneath; this is only an overlay.
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-[2px] overflow-y-auto overscroll-contain flex flex-col items-center sm:justify-center p-0 sm:p-6"
      data-testid="pay-checkout"
      role="dialog"
      aria-modal="true"
    >
      {inMeeting && (
        <button
          onClick={onBack}
          className="sticky top-0 z-10 w-full sm:max-w-[520px] sm:mb-3 sm:rounded-2xl h-16 px-4 flex items-center gap-2.5 bg-[#0b1f12] ring-1 ring-emerald-400/25 text-white text-[15px] font-semibold shadow-[0_10px_30px_-12px_rgba(0,0,0,1)]"
          data-testid="pay-back-to-class"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2.4} />
          Back to class
          <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-normal text-emerald-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Class still running
          </span>        </button>
      )}
      <motion.div
        initial={{ y: 24, scale: 0.98, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 28, stiffness: 320 }}
        className={`relative w-full ${
          wide ? "sm:max-w-[720px]" : "sm:max-w-[520px]"
        } sm:my-auto sm:max-h-[92vh] sm:overflow-y-auto sm:rounded-[20px] shadow-[0_30px_90px_-20px_rgba(0,0,0,0.8)] transition-[max-width] duration-300`}
      >
        {children}
      </motion.div>
    </motion.div>,
    document.body
  );
}

export function PayCheckout({ open, onClose, roomName, userName, whatsapp, classTitle, inMeeting = false }) {
  const [loading, setLoading] = useState(true);
  const [pay, setPay] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("main"); // main · returned
  const [kind, setKind] = useState("full");
  const [left, setLeft] = useState(300);
  const [revealed, setRevealed] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [myName, setMyName] = useState(userName || "");
  const [shot, setShot] = useState(null);
  const [shotUrl, setShotUrl] = useState(null);
  const [shotBusy, setShotBusy] = useState(false);
  const remoteRef = useRef("");
  const [gate, setGate] = useState(0); // seconds until "I have paid" unlocks
  const gateUntil = useRef(0);
  const openedAt = useRef(0);
  const qrRef = useRef(null);
  const fileRef = useRef(null);
  const payNowRef = useRef(null);
  const wentAway = useRef(false);
  const [aged, setAged] = useState(false);

  // ONE clock: 30s from the first time this student reached the checkout.
  // It is stored per payment, so coming back from a UPI app — or even a full
  // reload — never restarts the wait.
  useEffect(() => {
    if (!open || !pay?.id) return;
    const key = `udyog_pay_seen_${pay.id}`;
    let first = Number(localStorage.getItem(key) || 0);
    if (!first) {
      first = Date.now();
      localStorage.setItem(key, String(first));
    }
    gateUntil.current = first + 30000;
    const wait = gateUntil.current - Date.now();
    if (wait <= 0) return setAged(true);
    const t = setTimeout(() => setAged(true), wait);
    return () => clearTimeout(t);
  }, [open, pay?.id]);

  // Which step they were on survives a trip to a UPI app, an app switch, even
  // Android throwing the tab away and reloading it.
  useEffect(() => {
    if (!open || !pay?.id) return;
    const saved = sessionStorage.getItem(`udyog_pay_step_${pay.id}`);
    if (saved === "returned") setView("returned");
    const savedName = sessionStorage.getItem(`udyog_pay_name_${pay.id}`);
    if (savedName) setMyName(savedName);
  }, [open, pay?.id]);

  useEffect(() => {
    if (!open || !pay?.id) return;
    sessionStorage.setItem(`udyog_pay_step_${pay.id}`, view);
    if (myName.trim()) sessionStorage.setItem(`udyog_pay_name_${pay.id}`, myName.trim());
  }, [open, pay?.id, view, myName]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr("");
    api
      .post(`/meetings/${roomName}/pay/intent`, { user_name: userName, whatsapp: whatsapp || null, kind })
      .then(({ data }) => {
        setPay(data);
        // A student who already opened a UPI app or the QR — even in an earlier
        // session — keeps the confirm option. Nobody else ever sees it.
        if (["upi_opened", "qr_shown", "says_paid"].includes(data.stage)) setAttempted(true);
      })
      .catch(() => setErr("Fees aren't set up for this class yet — please message us on WhatsApp."))
      .finally(() => setLoading(false));
  }, [open, roomName, userName, whatsapp, kind]);

  // The strike-through + reveal plays once, not again on every view change.
  useEffect(() => {
    if (!open || revealed) return;
    const t = setTimeout(() => setRevealed(true), 1100);
    return () => clearTimeout(t);
  }, [open, revealed]);

  // Ticks the "you can confirm in Ns" lock.
  useEffect(() => {
    const t = setInterval(() => {
      const left = Math.ceil((gateUntil.current - Date.now()) / 1000);
      setGate(left > 0 ? left : 0);
    }, 500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!qrOpen) return;
    setLeft(300);
    setAttempted(true);
    stage("qr_shown");
    const scroll = setTimeout(
      () => qrRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      340
    );
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => {
      clearInterval(t);
      clearTimeout(scroll);
    };
  }, [qrOpen]);

  // The phone's back gesture must close the checkout, never leave the class —
  // in fullscreen there is no browser bar to fall back on.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open || !inMeeting) return;
    window.history.pushState({ udyogPay: true }, "");
    const onPop = () => closeRef.current?.();
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (window.history.state?.udyogPay) window.history.back();
    };
  }, [open, inMeeting]);

  useEffect(() => {
    if (!open) return;
    const onVis = () => {
      if (document.hidden) {
        wentAway.current = true;
        return;
      }
      if (openedAt.current && Date.now() - openedAt.current > 2500) {
        setView("returned");
      }
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (view === "main") onClose();
      else setView("main");
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, view, onClose]);

  const s = pay?.settings;
  const query = useMemo(() => {
    if (!pay || !s?.upi_vpa) return "";
    // Spaces must be %20, not "+" — some UPI apps reject the plus form.
    return new URLSearchParams({
      pa: s.upi_vpa,
      pn: s.upi_name || "Udyog",
      am: pay.amount,
      cu: "INR",
      tn: `${classTitle || "Udyog"} ${pay.code}`.slice(0, 45),
      tr: pay.code.replace(/[^A-Za-z0-9]/g, ""),
    })
      .toString()
      .replace(/\+/g, "%20");
  }, [pay, s, classTitle]);

  const stage = (st, extra = {}) => {
    if (!pay) return;
    api.post(`/meetings/${roomName}/pay/${pay.id}/stage`, { stage: st, ...extra }).catch(() => {});
  };

  // Generated on the device with the highest error correction, so it scans
  // even in bad light — no third-party QR service in the payment path.
  const [qrData, setQrData] = useState("");
  useEffect(() => {
    if (!query) return;
    QRCode.toDataURL(`upi://pay?${query}`, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 720,
      color: { dark: "#000000", light: "#ffffff" },
    })
      .then(setQrData)
      .catch(() => setQrData(""));
  }, [query]);

  const payWith = (app) => {
    openedAt.current = Date.now();
    wentAway.current = false;
    setAttempted(true);
    stage("upi_opened");
    window.location.href = app.scheme(query);
    // If the app never took over, the tab was never hidden — tell them plainly
    // and drop the QR open right there instead of leaving a dead tap.
    setTimeout(() => {
      if (document.hidden || wentAway.current) return;
      toast.error(`${app.name} didn't open on this device — scan the QR code instead`);
      setQrOpen(true);
    }, 2600);
  };

  const confirmPaid = async () => {
    const who = myName.trim();
    if (who.length < 2) return toast.error("Please type your full name");
    if (!shot) return toast.error("Attach your payment screenshot first");

    // The screenshot is uploaded the moment they pick it, so this tap does
    // nothing but open OUR WhatsApp chat — no app chooser, no waiting.
    if (remoteRef.current) {
      openWhatsApp(who, remoteRef.current);
      return;
    }

    // Upload still in flight: hold the tab open with a blank window so Chrome
    // doesn't treat the redirect as a blocked pop-up, then send it on.
    const win = window.open("", "_blank");
    setShotBusy(true);
    const url = await uploadShot(shot, who);
    setShotBusy(false);
    const link = waLink(payMsg(who, url));
    if (win && !win.closed) win.location.href = link;
    else window.open(link, "_blank", "noreferrer");
    toast.success("Opening WhatsApp — just hit send");
    onClose();
  };

  const openWhatsApp = (who, url) => {
    window.open(waLink(payMsg(who, url)), "_blank", "noreferrer");
    toast.success("Opening WhatsApp — just hit send");
    onClose();
  };

  const uploadShot = async (file, who) => {
    const form = new FormData();
    form.append("file", file);
    form.append("student_name", who || userName || "");
    try {
      const { data } = await api.post(`/meetings/${roomName}/pay/${pay.id}/screenshot`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      remoteRef.current = data?.screenshot_url || "";
      return remoteRef.current;
    } catch (_) {
      return "";
    }
  };

  useEffect(() => {
    if (!aged) return;
    const t = setTimeout(
      () => payNowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      400
    );
    return () => clearTimeout(t);
  }, [aged]);

  const pickShot = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type))
      return toast.error("Please pick a PNG or JPG screenshot");
    if (file.size > 8 * 1024 * 1024) return toast.error("That image is over 8 MB");
    setShot(file);
    setShotUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    // Upload straight away, while they're still typing their name, so the send
    // button has nothing left to wait for.
    remoteRef.current = "";
    uploadShot(file, myName.trim());
  };

  const waLink = (text, number) =>
    `https://wa.me/91${number || s?.whatsapp_number || ""}?text=${encodeURIComponent(text)}`;

  const paidMsg = (who) =>
    `Payment done ✅\nName: ${who}\nAmount: ₹${pay?.amount}`;

  // What lands in our chat: who paid, how much, and a tap-to-open picture of
  // their screenshot (the file itself is already on their record in the panel).
  const payMsg = (who, url) =>
    `${paidMsg(who)}\nRef: ${pay?.code || "—"}${url ? `\nScreenshot: ${url}` : ""}`;

  if (!open) return null;


  if (loading || err)
    return (
      <Shell inMeeting={inMeeting} onBack={onClose}>
        <div className="rounded-[20px] bg-white py-16 grid place-items-center">
          {err ? (
            <span className="px-8 text-center text-[13px] text-neutral-500" data-testid="pay-error">
              {err}
            </span>
          ) : (
            <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
          )}
        </div>
      </Shell>
    );

  const merchant = "Udyog";

  /* ------------------------- secondary views ------------------------- */
  if (view !== "main" && view !== "qr")
    return (
      <Shell inMeeting={inMeeting} onBack={onClose}>
        <div className="rounded-[20px] bg-white p-6">
          <div className="flex items-start gap-3 mb-4">
            <button
              onClick={() => setView("main")}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
              data-testid="pay-try-again"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5 text-neutral-900" />
            </button>
            <div className="flex-1 text-[18px] font-bold text-neutral-900 leading-snug">
              {view === "returned" && "Send your payment screenshot"}
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
              data-testid="pay-close"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-neutral-900" />
            </button>
          </div>

          {view === "returned" && (
            <div data-testid="pay-returned">
              <div className="text-[13px] text-neutral-500 -mt-2 mb-4">
                Attach the payment screenshot and tap send — it opens our WhatsApp chat with your
                name, the amount and the screenshot ready to go. One tap, nothing else.
              </div>

              <label className="block text-[12.5px] font-bold text-neutral-900 uppercase tracking-wide">
                Your full name
              </label>
              <input
                value={myName}
                onChange={(e) => setMyName(e.target.value.slice(0, 60))}
                placeholder="e.g. Oshinik Chakraborty"
                name="student-full-name"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="done"
                onFocus={(e) =>
                  setTimeout(
                    () => e.target.scrollIntoView({ behavior: "smooth", block: "center" }),
                    350
                  )
                }
                className="mt-1.5 w-full h-12 px-4 rounded-[12px] border border-neutral-200 focus:border-[#25D366] text-[14px] text-neutral-900 outline-none"
                data-testid="pay-shot-name"
              />

              <label className="block mt-3.5 text-[12.5px] font-bold text-neutral-900 uppercase tracking-wide">
                Payment screenshot
              </label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => pickShot(e.target.files?.[0])}
                data-testid="pay-shot-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className={`mt-1.5 w-full text-left flex items-center gap-3.5 px-4 py-3.5 rounded-[12px] border cursor-pointer transition-colors ${
                  shot ? "border-[#25D366] bg-[#f1f7f2]" : "border-dashed border-neutral-300 hover:border-neutral-500"
                }`}
                data-testid="pay-shot-picker"
              >
                {shotUrl ? (
                  <img
                    src={shotUrl}
                    alt="Payment screenshot"
                    className="w-14 h-14 rounded-[8px] object-cover ring-1 ring-[#25D366]/40"
                    data-testid="pay-shot-preview"
                  />
                ) : (
                  <span className="w-14 h-14 shrink-0 rounded-[8px] bg-[#0b1f12] grid place-items-center text-white">
                    <ImagePlus className="w-6 h-6" strokeWidth={1.7} />
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-bold text-neutral-900">
                    {shot ? "Screenshot attached" : "Pick from your gallery"}
                  </span>
                  <span className="block text-[12.5px] text-neutral-500 truncate">
                    {shot ? `${shot.name} · tap to change` : "Tap anywhere on this box"}
                  </span>
                </span>
              </button>

              <button
                onClick={confirmPaid}
                disabled={shotBusy || !shot || myName.trim().length < 2}
                className={`w-full h-14 mt-3 rounded-[12px] font-bold text-[13.5px] flex items-center justify-center gap-2 transition-all ${
                  shotBusy || !shot || myName.trim().length < 2
                    ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                    : "bg-[#25D366] text-white hover:brightness-105"
                }`}
                data-testid="pay-send-screenshot"
              >
                {shotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <WaMark size={18} />}
                {shotBusy
                  ? "Sending…"
                  : "Submit & send on WhatsApp"}
              </button>
              <div className="text-[12px] text-neutral-400 text-center mt-2.5">
                Goes straight to our WhatsApp — no other app in between.
              </div>
              <button
                onClick={() => setView("main")}
                className="w-full h-12 mt-3 rounded-[12px] border border-neutral-200 text-[13px] text-neutral-700 hover:bg-neutral-50"
                data-testid="pay-not-paid-back"
              >
                Not paid yet — go back
              </button>
            </div>
          )}


        </div>
      </Shell>
    );

  /* --------------------------- main checkout --------------------------- */
  return (
    <Shell wide={qrOpen} inMeeting={inMeeting} onBack={onClose}>
      <div className="rounded-[20px] overflow-hidden bg-white" data-testid="pay-apps">
        {/* Header */}
        <div className="relative px-5 pt-4 pb-5 bg-[radial-gradient(120%_120%_at_15%_0%,#0d2c19_0%,#04140b_45%,#000000_100%)]">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-full text-white/80 hover:bg-white/10 hover:text-white transition-colors"
            data-testid="pay-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3.5 pr-10">
            <span className="w-[66px] h-[66px] shrink-0 rounded-full ring-[3px] ring-[#22c55e] shadow-[0_0_18px_-4px_rgba(34,197,94,0.8)] grid place-items-center">
              <img src="/upi/udyog-word.png" alt="Udyog" className="w-[46px]" />
            </span>
            <div className="leading-tight">
              <div className="text-white text-[19px] font-bold">Pay your fees</div>
              <div className="text-white/55 text-[12.5px]">{s.course_name || "Udyog Basic Course"}</div>
            </div>
            <div className="ml-auto hidden sm:flex items-center gap-2">
              <ShieldCheck className="w-[19px] h-[19px] text-[#22c55e]" />
              <span className="text-white text-[13px] font-semibold whitespace-nowrap">Secure & Safe</span>
            </div>
          </div>

          {/* Amount */}
          <div className="mt-4 rounded-[14px] border border-[#22c55e]/45 bg-[#04170c]/60 px-4 py-3 shadow-[0_0_28px_-12px_rgba(34,197,94,0.65)]">
            <div className="flex items-center">
              <div className="w-[42%] text-center">
                <div className="text-white/70 text-[12.5px]">Total Amount</div>
                <div className="relative inline-block mt-0.5">
                  <span className="text-white/80 text-[19px] font-semibold tabular-nums">
                    ₹{money(s.anchor_amount || pay.base_amount)}
                  </span>
                  <motion.span
                    initial={{ scaleX: revealed ? 1 : 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.35, delay: revealed ? 0 : 0.9 }}
                    style={{ originX: 0 }}
                    className="absolute left-[-2px] right-[-2px] top-1/2 h-[1.5px] bg-[#e0402c] rounded-full"
                  />
                </div>
              </div>
              <div className="w-px self-stretch bg-white/15" />
              <div className="flex-1 text-center">
                <div className="flex items-center justify-center gap-2">
                  <span className="w-8 h-px bg-gradient-to-r from-transparent to-[#22c55e]/70" />
                  <span className="w-[5px] h-[5px] rounded-full bg-[#22c55e]" />
                  <span className="text-[#22c55e] text-[13px] font-semibold">You Pay</span>
                  <span className="w-[5px] h-[5px] rounded-full bg-[#22c55e]" />
                  <span className="w-8 h-px bg-gradient-to-l from-transparent to-[#22c55e]/70" />
                </div>
                <motion.div
                  initial={revealed ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: revealed ? 0 : 1, type: "spring", stiffness: 300, damping: 24 }}
                  className="text-white text-[32px] font-extrabold tracking-tight tabular-nums leading-none mt-1"
                >
                  ₹{money(pay.base_amount)}
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 pt-4 pb-4">
          <div className="flex items-center gap-3.5">
            <span className="w-12 h-12 rounded-full bg-[#0b1f12] grid place-items-center">
              <Store className="w-[22px] h-[22px] text-white" strokeWidth={1.8} />
            </span>
            <span className="leading-tight">
              <span className="block text-[13px] text-neutral-500">Paying to</span>
              <span className="block text-[20px] font-bold text-neutral-900">{merchant}</span>
            </span>
          </div>

          <div className="h-px bg-neutral-200 my-4" />

          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-[18px] font-bold text-neutral-900">Choose a payment method</span>
            <span className="px-2 py-[3px] rounded-[5px] bg-[#e6f6ea] text-[#1f7a3d] text-[11.5px] font-medium">
              Recommended
            </span>
          </div>
          <div className="text-[13.5px] text-neutral-500 mt-1">
            Pay securely using your preferred UPI app
          </div>

          <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-x-2 gap-y-3">
            {APPS.map((a) => (
              <button
                key={a.id}
                onClick={() => payWith(a)}
                className="group flex flex-col items-center gap-2"
                data-testid={`pay-app-${a.id}`}
              >
                <img
                  src={a.src}
                  alt={a.name}
                  className="w-[48px] h-[48px] sm:w-[54px] sm:h-[54px] rounded-full transition-transform duration-200 group-hover:-translate-y-1 group-active:scale-95"
                />
                <span className="text-[12px] text-neutral-800 text-center leading-tight">{a.name}</span>
              </button>
            ))}
          </div>

          <div className="mt-3.5 flex items-center">
            <span className="flex-1 h-px bg-neutral-200" />
            <span className="mx-3 text-[11px] uppercase tracking-[0.28em] text-neutral-400/80">
              or
            </span>
            <span className="flex-1 h-px bg-neutral-200" />
          </div>

          <button
            onClick={() => setQrOpen((v) => !v)}
            className={`mt-3 w-full flex items-center gap-4 px-4 py-3 rounded-[12px] border transition-colors text-left ${
              qrOpen ? "border-neutral-400 bg-neutral-50" : "border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50"
            }`}
            data-testid="pay-show-qr"
          >
            <span className="w-[52px] h-[52px] shrink-0 rounded-[10px] bg-[#0b1f12] grid place-items-center">
              <svg viewBox="0 0 26 26" className="w-[30px] h-[30px]" fill="none">
                <rect x="2" y="2" width="9" height="9" rx="2" stroke="#ffffff" strokeWidth="2.4" />
                <rect x="15" y="2" width="9" height="9" rx="2" stroke="#ffffff" strokeWidth="2.4" />
                <rect x="2" y="15" width="9" height="9" rx="2" stroke="#ffffff" strokeWidth="2.4" />
                <rect x="15" y="15" width="4" height="4" rx="1" fill="#ffffff" />
                <rect x="20" y="20" width="4" height="4" rx="1" fill="#ffffff" />
                <rect x="15" y="21" width="3" height="3" rx="1" fill="#ffffff" />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-[16px] font-bold text-neutral-900">Pay by QR Code</span>
              <span className="block text-[13.5px] text-neutral-500">Scan & pay using any UPI app</span>
            </span>
            <ChevronRight
              className={`w-6 h-6 text-neutral-400 ml-auto shrink-0 transition-transform duration-300 ${
                qrOpen ? "rotate-90" : ""
              }`}
            />
          </button>

          {/* The QR expands right here, inside the same card */}
          {qrOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.28 }}
              className="overflow-hidden"
              data-testid="pay-qr"
              ref={qrRef}
            >
              <div className="mt-3 grid sm:grid-cols-[286px_1fr] gap-5 sm:gap-8 p-4 rounded-[12px] border border-neutral-200">
                <div className="relative mx-auto w-[248px] sm:w-[286px] max-w-full aspect-square rounded-[12px] border border-neutral-200 p-3 bg-white">
                  {s.qr_url || qrData ? (
                    <img
                      src={s.qr_url || qrData}
                      alt="UPI QR code"
                      className="w-full h-full object-contain"
                      data-testid="pay-qr-image"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-[17px] font-bold text-neutral-900">Payment Details</div>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[14px] text-neutral-500">Paying to</span>
                      <span className="text-[14px] font-semibold text-neutral-900">{merchant}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[14px] text-neutral-500">Amount to pay</span>
                      <button
                        onClick={async () => {
                          const ok = await copyText(pay.amount);
                          toast[ok ? "success" : "error"](ok ? "Amount copied" : "Couldn't copy");
                        }}
                        className="text-[15px] font-bold text-neutral-900 hover:text-emerald-700 tabular-nums inline-flex items-center gap-1.5"
                        data-testid="pay-copy-amount"
                      >
                        ₹{pay.amount} <Copy className="w-3.5 h-3.5 text-neutral-400" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[14px] text-neutral-500">UPI ID</span>
                      <button
                        onClick={async () => {
                          const ok = await copyText(s.upi_vpa);
                          toast[ok ? "success" : "error"](ok ? "UPI ID copied" : "Couldn't copy");
                        }}
                        className="text-[14px] font-semibold text-neutral-900 hover:text-emerald-700 truncate max-w-[230px]"
                        data-testid="pay-copy-vpa"
                      >
                        {s.upi_vpa}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-3 p-4 rounded-[10px] bg-[#f1f7f2] border border-[#dce9de]">
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full border-2 border-emerald-600 grid place-items-center text-emerald-700 text-[11px] font-bold">
                      !
                    </span>
                    <span>
                      <span className="block text-[14px] font-semibold text-neutral-900">
                        {s.qr_url
                          ? `Scan, then pay ₹${pay.amount}`
                          : "Scan the QR code using any UPI app"}
                      </span>
                      <span className="block text-[13.5px] text-neutral-600 leading-snug">
                        {s.qr_url
                          ? "Then come back here and send us the payment screenshot."
                          : `Amount ₹${pay.amount} is already filled in. Make sure the name shows as ${merchant} before you pay.`}
                      </span>
                    </span>
                  </div>

                  <div
                    className="mt-4 flex items-center gap-2 text-[14px] text-neutral-500"
                    data-testid="pay-qr-timer"
                  >
                    <Clock className="w-4 h-4 text-neutral-400" />
                    QR Code valid for{" "}
                    <span className="text-emerald-600 font-semibold tabular-nums">
                      {String(Math.floor(left / 60)).padStart(2, "0")}:
                      {String(left % 60).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Always on the page so nobody has to hunt for it — but locked for
              the first 30 seconds, so it can't be tapped before there is any
              chance of a payment actually having been made. */}
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setView("returned")}
            disabled={!aged}
            className={`mt-3 w-full min-h-[58px] rounded-[14px] px-3 py-4 flex items-center justify-center gap-2.5 transition-all ${
              aged
                ? "bg-[#25D366] hover:brightness-105 shadow-[0_12px_28px_-12px_rgba(37,211,102,0.95)]"
                : "bg-neutral-100 border border-neutral-200 cursor-not-allowed"
            }`}
            ref={payNowRef}
            data-testid="pay-already-paid"
          >
            <WaMark size={21} muted={!aged} />
            <span
              className={`text-[13.5px] sm:text-[15px] font-bold whitespace-nowrap ${
                aged ? "text-white" : "text-neutral-400"
              }`}
            >
              {aged
                ? "Already paid? Send payment screenshot to us"
                : `Send payment screenshot · in ${gate}s`}
            </span>
          </motion.button>
          <div className="mt-2 text-center text-[12.5px] text-neutral-500">
            {aged
              ? "Send it on WhatsApp to confirm your admission"
              : "Pay first — this opens on its own in a few seconds"}
          </div>

          <div className="mt-3 flex items-center gap-2.5 px-4 py-2.5 rounded-[12px] bg-[#f7f7f7] border border-neutral-200">
            <ShieldCheck className="w-[22px] h-[22px] shrink-0 text-[#0b1f12]" strokeWidth={1.8} />
            <span className="text-[12.5px] text-neutral-600 leading-snug">
              100% secure · we never store your UPI details
            </span>
          </div>

          <div className="mt-3 text-center text-[12.5px] text-neutral-500">
            By proceeding, you agree to our{" "}
            <span className="text-[#1f7a3d] font-medium">Terms &amp; Conditions</span>
          </div>
          <div className="mt-1.5 text-center text-[12.5px] text-neutral-500">
            Need Help?{" "}
            <a
              href={waLink(`Hi, I need help with my payment.\nName: ${userName}\nAmount: ₹${pay.amount}`)}
              target="_blank"
              rel="noreferrer"
              className="text-[#1f7a3d] font-medium"
              data-testid="pay-support"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </Shell>
  );
}
